import TaxiCorporationUnbilledOrdersListParams from "../../../../../common/services/taxi-corporation/unbilled/orders-list/taxi-corporation-unbilled-orders-list-params";
import UnbilledOrder from "../../../../../common/types/unbilled-order";
import paginationService from "../../../../../common/utils/pagination/pagination.service";
import BillingsTaxiUnbilledPrivateOrderListingFilter, {
  BillingsTaxiUnbilledPrivateOrderListingClientFilter,
  BillingsTaxiUnbilledPrivateOrderListingDispatchFilter,
  BillingsTaxiUnbilledPrivateOrderListingDriverFilter,
  BillingsTaxiUnbilledPrivateOrderListingExcludeClientFilter,
  BillingsTaxiUnbilledPrivateOrderListingExternalOrderIdFilter,
  BillingsTaxiUnbilledPrivateOrderListingInternalOrderIdFilter,
  BillingsTaxiUnbilledPrivateOrderListingPassengerFilter,
  BillingsTaxiUnbilledPrivateOrderListingRouteAddressFilter,
  BillingsTaxiUnbilledPrivateOrderListingRouteDestinationAddressFilter,
  BillingsTaxiUnbilledPrivateOrderListingRouteIntermediateAddressFilter,
  BillingsTaxiUnbilledPrivateOrderListingRoutePickupAddressFilter,
  BillingsTaxiUnbilledPrivateOrderListingStartDateFilter,
  BillingsTaxiUnbilledPrivateOrderListingStatusFilter,
} from "./types/billings-taxi-unbilled-private-order-listing-filter";
import BillingsTaxiUnbilledPrivateOrderListingFilterType from "./types/billings-taxi-unbilled-private-order-listing-filter-type";
import BillingsTaxiUnbilledPrivateOrderListingItemStatus from "./types/billings-taxi-unbilled-private-order-listing-item-status";
import BillingsTaxiUnbilledPrivateOrderListingSortKey from "./types/billings-taxi-unbilled-private-order-listing-sort-key";

const createRequestIsCancelled = (
  filters: BillingsTaxiUnbilledPrivateOrderListingFilter[],
): boolean | undefined => {
  const filterValue = filters.find(
    (filter) =>
      filter.type === BillingsTaxiUnbilledPrivateOrderListingFilterType.STATUS,
  )?.value as
    | BillingsTaxiUnbilledPrivateOrderListingStatusFilter["value"]
    | undefined;

  if (filterValue === undefined) {
    return undefined;
  }

  if (
    filterValue === BillingsTaxiUnbilledPrivateOrderListingItemStatus.CANCELLED
  ) {
    return true;
  }

  return false;
};

const createRequestOrder = (
  sortKey: BillingsTaxiUnbilledPrivateOrderListingSortKey | null,
): UnbilledOrder | undefined => {
  const options: {
    sortKey: BillingsTaxiUnbilledPrivateOrderListingSortKey;
    requestOrder: UnbilledOrder;
  }[] = [
    {
      requestOrder: UnbilledOrder.HUMAN_ID_ASC,
      sortKey:
        BillingsTaxiUnbilledPrivateOrderListingSortKey.INTERNAL_ORDER_ID_ASC,
    },
    {
      requestOrder: UnbilledOrder.HUMAN_ID_DESC,
      sortKey:
        BillingsTaxiUnbilledPrivateOrderListingSortKey.INTERNAL_ORDER_ID_DESC,
    },
    {
      requestOrder: UnbilledOrder.START_TIME_ASC,
      sortKey: BillingsTaxiUnbilledPrivateOrderListingSortKey.START_DATE_ASC,
    },
    {
      requestOrder: UnbilledOrder.START_TIME_DESC,
      sortKey: BillingsTaxiUnbilledPrivateOrderListingSortKey.START_DATE_DESC,
    },
  ];

  return options.find((option) => option.sortKey === sortKey)?.requestOrder;
};

const createParams = (
  page: number,
  pageSize: number,
  taxiCorporationUuid: string,
  filters: BillingsTaxiUnbilledPrivateOrderListingFilter[],
  sortKey: BillingsTaxiUnbilledPrivateOrderListingSortKey | null,
): TaxiCorporationUnbilledOrdersListParams => {
  const offset = paginationService.calculateOffset(page, pageSize);

  return {
    order: createRequestOrder(sortKey),
    taxiCorporationUuid,
    address: filters.find(
      (filter) =>
        filter.type ===
        BillingsTaxiUnbilledPrivateOrderListingFilterType.ROUTE_ADDRESS,
    )?.value as
      | BillingsTaxiUnbilledPrivateOrderListingRouteAddressFilter["value"]
      | undefined,
    limit: pageSize,
    client: filters.find(
      (filter) =>
        filter.type ===
        BillingsTaxiUnbilledPrivateOrderListingFilterType.CLIENT,
    )?.value as
      | BillingsTaxiUnbilledPrivateOrderListingClientFilter["value"]
      | undefined,
    excludeCargoCompany: filters.find(
      (filter) =>
        filter.type ===
        BillingsTaxiUnbilledPrivateOrderListingFilterType.EXCLUDE_CARGO_COMPANY,
    )?.value as
      | BillingsTaxiUnbilledPrivateOrderListingExcludeClientFilter["value"]
      | undefined,
    dispatch: filters.find(
      (filter) =>
        filter.type ===
        BillingsTaxiUnbilledPrivateOrderListingFilterType.DISPATCH,
    )?.value as
      | BillingsTaxiUnbilledPrivateOrderListingDispatchFilter["value"]
      | undefined,
    driver: filters.find(
      (filter) =>
        filter.type ===
        BillingsTaxiUnbilledPrivateOrderListingFilterType.DRIVER,
    )?.value as
      | BillingsTaxiUnbilledPrivateOrderListingDriverFilter["value"]
      | undefined,
    endAddress: filters.find(
      (filter) =>
        filter.type ===
        BillingsTaxiUnbilledPrivateOrderListingFilterType.ROUTE_DESTINATION_ADDRESS,
    )?.value as
      | BillingsTaxiUnbilledPrivateOrderListingRouteDestinationAddressFilter["value"]
      | undefined,
    externalId: filters.find(
      (filter) =>
        filter.type ===
        BillingsTaxiUnbilledPrivateOrderListingFilterType.EXTERNAL_ORDER_ID,
    )?.value as
      | BillingsTaxiUnbilledPrivateOrderListingExternalOrderIdFilter["value"]
      | undefined,
    humanId: filters.find(
      (filter) =>
        filter.type ===
        BillingsTaxiUnbilledPrivateOrderListingFilterType.INTERNAL_ORDER_ID,
    )?.value as
      | BillingsTaxiUnbilledPrivateOrderListingInternalOrderIdFilter["value"]
      | undefined,
    isCancelled: createRequestIsCancelled(filters),
    midAddress: filters.find(
      (filter) =>
        filter.type ===
        BillingsTaxiUnbilledPrivateOrderListingFilterType.ROUTE_INTERMEDIATE_ADDRESS,
    )?.value as
      | BillingsTaxiUnbilledPrivateOrderListingRouteIntermediateAddressFilter["value"]
      | undefined,
    offset,
    passenger: filters.find(
      (filter) =>
        filter.type ===
        BillingsTaxiUnbilledPrivateOrderListingFilterType.PASSENGER,
    )?.value as
      | BillingsTaxiUnbilledPrivateOrderListingPassengerFilter["value"]
      | undefined,
    startAddress: filters.find(
      (filter) =>
        filter.type ===
        BillingsTaxiUnbilledPrivateOrderListingFilterType.ROUTE_PICKUP_ADDRESS,
    )?.value as
      | BillingsTaxiUnbilledPrivateOrderListingRoutePickupAddressFilter["value"]
      | undefined,
    startTimeSince: (
      filters.find(
        (filter) =>
          filter.type ===
          BillingsTaxiUnbilledPrivateOrderListingFilterType.START_DATE,
      )?.value as
        | BillingsTaxiUnbilledPrivateOrderListingStartDateFilter["value"]
        | undefined
    )?.from?.toJSON(),
    startTimeTo: (
      filters.find(
        (filter) =>
          filter.type ===
          BillingsTaxiUnbilledPrivateOrderListingFilterType.START_DATE,
      )?.value as
        | BillingsTaxiUnbilledPrivateOrderListingStartDateFilter["value"]
        | undefined
    )?.to?.toJSON(),
  };
};

const billingsTaxiUnbilledPrivateOrderListingRequestFactory = {
  createParams,
};

export default billingsTaxiUnbilledPrivateOrderListingRequestFactory;
